import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BadgeCheck, Check, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Circle, Copy, ExternalLink, Pencil, Plus, RefreshCw, Save, Search, Star, UploadCloud, X } from "lucide-react";
import { NavLink } from "react-router-dom";
import {
    downloadPriceMonitorConfig,
    downloadPriceMonitorExcel,
    getCatalogImportRuns,
    getPriceMonitorProducts,
    getPriceMonitorCompetitorFeedUrl,
    getPriceMonitorCompetitorRefreshStatus,
    getPriceMonitorOwnPricesRefreshStatus,
    importPriceMonitorConfig,
    importPriceMonitorExcel,
    importPriceMonitorFeed,
    refreshPriceMonitorCompetitor,
    refreshPriceMonitorCompetitorAll,
    refreshPriceMonitorOwnPrice,
    refreshPriceMonitorOwnPricesAll,
    updatePriceMonitorCompetitor,
    updatePriceMonitorCompetitorVerification,
    updatePriceMonitorDifferenceReview,
    updatePriceMonitorProduct,
    updatePriceMonitorFavorite,
} from "../services/api";

const emptyCompetitor = { name: "", price: "", url: "" };
const pageSize = 100;
const defaultYmlUrl = "https://azs.parts/yml.xml";
const jsonLdCompetitorNames = new Set(["azs.pro", "azspro", "azs pro", "aztgrup", "aztgrup.ru", "azt grup"]);
const defaultStructuredCompetitors = ["azs.pro", "aztgrup"];

const isJsonLdCompetitor = (name) => jsonLdCompetitorNames.has(String(name || "").trim().toLowerCase());
const isPlaceholderCompetitor = (name) => String(name || "").startsWith("Конкурент ");
const competitorKey = (name) => String(name || "").trim().toLowerCase();
const preferredCompetitorNames = ["Vinso", ...defaultStructuredCompetitors];
const verificationMeta = {
    unchecked: { label: "Не проверено", short: "○", className: "verification-unchecked" },
    match: { label: "Проверено: то", short: "✓", className: "verification-match" },
    question: { label: "Сомнительно", short: "?", className: "verification-question" },
    wrong: { label: "Не то", short: "×", className: "verification-wrong" },
};
const verificationOptions = [
    { status: "unchecked", icon: Circle, title: "Сбросить проверку" },
    { status: "match", icon: Check, title: "То" },
    { status: "question", icon: AlertTriangle, title: "Сомнительно" },
    { status: "wrong", icon: X, title: "Не то" },
];

const differenceReasonLabels = {
    manufacturer: "Другой производитель",
    configuration: "Другая комплектация",
    other: "Другая причина",
};

const findCompetitorByName = (competitors, name) => (
    (competitors || []).find((competitor) => competitorKey(competitor?.name) === competitorKey(name))
);

const competitorVerificationStatus = (competitor) => (
    verificationMeta[competitor?.verification_status] ? competitor.verification_status : "unchecked"
);

const formatStatusDate = (value) => {
    if (!value) {
        return "";
    }
    return new Date(value).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const downloadBlob = (blob, fallbackFilename) => {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fallbackFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
};

const filenameFromResponse = (response, fallbackFilename) => {
    const contentDisposition = response.headers["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return filenameMatch?.[1] || fallbackFilename;
};

const normalizePrice = (value) => {
    const cleanValue = String(value ?? "").replace(",", ".").trim();
    return cleanValue === "" ? null : cleanValue;
};

const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") {
        return "нет цены";
    }
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
        return String(value);
    }
    return `${numberValue.toLocaleString("ru-RU")} ₽`;
};

const copyTextFallback = (text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
};

const PriceMonitoring = ({ favoriteOnly = false }) => {
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [priceState, setPriceState] = useState("all");
    const [differenceState, setDifferenceState] = useState("all");
    const [differenceDirection, setDifferenceDirection] = useState("any");
    const [differenceUnit, setDifferenceUnit] = useState("rub");
    const [minimumDifference, setMinimumDifference] = useState("500");
    const [appliedMinimumDifference, setAppliedMinimumDifference] = useState("500");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [offset, setOffset] = useState(0);
    const [editingCode, setEditingCode] = useState(null);
    const [draft, setDraft] = useState(null);
    const [importUrl, setImportUrl] = useState(defaultYmlUrl);
    const [importTarget, setImportTarget] = useState("own");
    const [competitorName, setCompetitorName] = useState("");
    const [importFile, setImportFile] = useState(null);
    const [importRuns, setImportRuns] = useState([]);
    const [message, setMessage] = useState("");
    const [copiedKey, setCopiedKey] = useState("");
    const [competitorModal, setCompetitorModal] = useState(null);
    const [differenceModal, setDifferenceModal] = useState(null);
    const [differenceReason, setDifferenceReason] = useState("manufacturer");
    const [differenceNote, setDifferenceNote] = useState("");
    const [differenceSaving, setDifferenceSaving] = useState(false);
    const [competitorDraft, setCompetitorDraft] = useState(emptyCompetitor);
    const [refreshingKey, setRefreshingKey] = useState("");
    const [verificationSavingKey, setVerificationSavingKey] = useState("");
    const [pageInput, setPageInput] = useState("1");
    const [refreshStatuses, setRefreshStatuses] = useState({});
    const [ownPriceRefreshStatus, setOwnPriceRefreshStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [favoriteSavingCode, setFavoriteSavingCode] = useState("");

    const currentPage = Math.floor(offset / pageSize) + 1;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    const paginationItems = useMemo(() => {
        if (pageCount <= 7) {
            return Array.from({ length: pageCount }, (_, index) => index + 1);
        }

        const pages = new Set([1, pageCount]);
        for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
            if (page > 1 && page < pageCount) {
                pages.add(page);
            }
        }

        const sortedPages = [...pages].sort((left, right) => left - right);
        return sortedPages.reduce((items, page, index) => {
            if (index > 0 && page - sortedPages[index - 1] > 1) {
                items.push("ellipsis-" + sortedPages[index - 1]);
            }
            items.push(page);
            return items;
        }, []);
    }, [currentPage, pageCount]);

    useEffect(() => {
        setPageInput(String(currentPage));
    }, [currentPage]);

    const loadProducts = useCallback(async () => {
        setIsLoading(true);
        setMessage("");
        try {
            const response = await getPriceMonitorProducts({
                favorite_only: favoriteOnly,
                q: appliedQuery || undefined,
                price_state: priceState,
                difference_state: differenceState,
                difference_direction: differenceDirection,
                difference_unit: differenceUnit,
                min_difference: appliedMinimumDifference,
                sort_by: sortBy,
                sort_dir: sortDir,
                limit: pageSize,
                offset,
            });
            setProducts(response.data.items);
            setTotal(response.data.total);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить мониторинг цен.");
        } finally {
            setIsLoading(false);
        }
    }, [appliedQuery, offset, priceState, differenceState, differenceDirection, differenceUnit, appliedMinimumDifference, sortBy, sortDir, favoriteOnly]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const loadImportRuns = useCallback(async () => {
        try {
            const response = await getCatalogImportRuns({ limit: 5 });
            setImportRuns(response.data);
        } catch (err) {
            setImportRuns([]);
        }
    }, []);

    useEffect(() => {
        loadImportRuns();
    }, [loadImportRuns]);

    const competitorColumnNames = useMemo(() => {
        const namesByKey = new Map();
        const addName = (name) => {
            const cleanName = String(name || "").trim();
            if (!cleanName || isPlaceholderCompetitor(cleanName)) {
                return;
            }
            const key = competitorKey(cleanName);
            if (!namesByKey.has(key)) {
                namesByKey.set(key, cleanName);
            }
        };
        products.forEach((product) => {
            (product.competitors || []).forEach((competitor) => addName(competitor?.name));
        });
        importRuns.forEach((run) => {
            if (run.import_target === "competitor") {
                addName(run.competitor_name);
            }
        });
        defaultStructuredCompetitors.forEach(addName);

        const orderedNames = [];
        preferredCompetitorNames.forEach((preferredName) => {
            const key = competitorKey(preferredName);
            if (namesByKey.has(key)) {
                orderedNames.push(namesByKey.get(key));
                namesByKey.delete(key);
            }
        });
        namesByKey.forEach((name) => orderedNames.push(name));
        while (orderedNames.length < 3) {
            orderedNames.push(`Конкурент ${orderedNames.length + 1}`);
        }
        return orderedNames.slice(0, 6);
    }, [importRuns, products]);

    const visibleCompetitorCount = competitorColumnNames.length;

    const refreshableCompetitorNames = useMemo(() => (
        competitorColumnNames.filter((name) => name && !isPlaceholderCompetitor(name))
    ), [competitorColumnNames]);

    const loadRefreshStatuses = useCallback(async () => {
        const uniqueNames = Array.from(new Set(refreshableCompetitorNames));
        if (!uniqueNames.length) {
            setRefreshStatuses({});
            return;
        }
        const responses = await Promise.all(uniqueNames.map(async (name) => {
            try {
                const response = await getPriceMonitorCompetitorRefreshStatus(name, favoriteOnly);
                return [name, response.data];
            } catch (err) {
                return [name, null];
            }
        }));
        setRefreshStatuses((current) => {
            const nextStatuses = { ...current };
            responses.forEach(([name, status]) => {
                if (status) {
                    nextStatuses[name] = status;
                }
            });
            return nextStatuses;
        });
    }, [refreshableCompetitorNames, favoriteOnly]);

    useEffect(() => {
        loadRefreshStatuses();
    }, [loadRefreshStatuses]);

    const loadOwnPriceRefreshStatus = useCallback(async () => {
        try {
            const response = await getPriceMonitorOwnPricesRefreshStatus(favoriteOnly);
            setOwnPriceRefreshStatus(response.data);
        } catch (err) {
            setOwnPriceRefreshStatus(null);
        }
    }, [favoriteOnly]);

    useEffect(() => {
        loadOwnPriceRefreshStatus();
    }, [loadOwnPriceRefreshStatus]);

    useEffect(() => {
        const hasActiveRefresh = Object.values(refreshStatuses).some((status) => status?.active);
        if (!hasActiveRefresh) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            loadRefreshStatuses();
        }, 30000);
        return () => window.clearInterval(intervalId);
    }, [loadRefreshStatuses, refreshStatuses]);

    useEffect(() => {
        if (!ownPriceRefreshStatus?.active) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            loadOwnPriceRefreshStatus();
            loadProducts();
        }, 30000);
        return () => window.clearInterval(intervalId);
    }, [loadOwnPriceRefreshStatus, loadProducts, ownPriceRefreshStatus]);

    const startEdit = (product) => {
        setEditingCode(product.product_code);
        const sourceCompetitors = product.competitors || [];
        const alignedCompetitors = competitorColumnNames.map((columnName) => {
            const existingCompetitor = findCompetitorByName(sourceCompetitors, columnName);
            if (existingCompetitor) {
                return existingCompetitor;
            }
            return isPlaceholderCompetitor(columnName) ? { ...emptyCompetitor } : { ...emptyCompetitor, name: columnName };
        });
        sourceCompetitors.forEach((competitor) => {
            if (competitor?.name && !alignedCompetitors.some((item) => competitorKey(item.name) === competitorKey(competitor.name))) {
                alignedCompetitors.push(competitor);
            }
        });
        setDraft({
            our_price: product.our_price ?? "",
            competitors: alignedCompetitors.map((competitor) => ({
                name: competitor.name || "",
                price: competitor.price ?? "",
                url: competitor.url || "",
                url_locked: Boolean(competitor.url_locked),
                url_source: competitor.url_source || null,
                verification_status: competitor.verification_status || null,
                verification_note: competitor.verification_note || null,
                verified_by: competitor.verified_by || null,
                verified_at: competitor.verified_at || null,
                price_difference_reason: competitor.price_difference_reason || null,
                price_difference_note: competitor.price_difference_note || null,
                price_difference_by: competitor.price_difference_by || null,
                price_difference_at: competitor.price_difference_at || null,
            })),
            note: product.note || "",
        });
    };

    const cancelEdit = () => {
        setEditingCode(null);
        setDraft(null);
    };

    const updateDraftCompetitor = (index, field, value) => {
        setDraft((current) => ({
            ...current,
            competitors: current.competitors.map((competitor, competitorIndex) => (
                competitorIndex === index ? { ...competitor, [field]: value } : competitor
            )),
        }));
    };

    const addCompetitor = () => {
        setDraft((current) => ({
            ...current,
            competitors: [...current.competitors, { ...emptyCompetitor }],
        }));
    };

    const removeCompetitor = (index) => {
        setDraft((current) => ({
            ...current,
            competitors: current.competitors.filter((_, competitorIndex) => competitorIndex !== index),
        }));
    };

    const saveProduct = async (product) => {
        setIsSaving(true);
        setMessage("");
        try {
            const payload = {
                our_price: normalizePrice(draft.our_price),
                competitors: draft.competitors.map((competitor) => ({
                    name: competitor.name.trim(),
                    price: normalizePrice(competitor.price),
                    url: competitor.url.trim() || null,
                    url_locked: Boolean(competitor.url_locked),
                    url_source: competitor.url_source || null,
                    verification_status: competitor.verification_status || null,
                    verification_note: competitor.verification_note || null,
                    verified_by: competitor.verified_by || null,
                    verified_at: competitor.verified_at || null,
                    price_difference_reason: competitor.price_difference_reason || null,
                    price_difference_note: competitor.price_difference_note || null,
                    price_difference_by: competitor.price_difference_by || null,
                    price_difference_at: competitor.price_difference_at || null,
                })).filter((competitor) => competitor.name && (competitor.price !== null || competitor.url)),
                note: draft.note.trim() || null,
            };
            const response = await updatePriceMonitorProduct(product.product_code, payload);
            setProducts((current) => current.map((item) => (
                item.product_code === product.product_code ? response.data : item
            )));
            cancelEdit();
            setMessage("Цены сохранены.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить цены.");
        } finally {
            setIsSaving(false);
        }
    };

    const copyPrice = async (value, key) => {
        const text = value === null || value === undefined ? "" : String(value);
        if (!text) {
            return;
        }
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else if (!copyTextFallback(text)) {
                throw new Error("Fallback copy failed");
            }
            setCopiedKey(key);
            setMessage("Цена скопирована.");
            window.setTimeout(() => setCopiedKey(""), 1200);
        } catch (err) {
            setMessage("Не удалось скопировать цену. Выделите значение вручную.");
        }
    };

    const updateProductInList = (nextProduct) => {
        setProducts((current) => current.map((item) => (
            item.product_code === nextProduct.product_code ? nextProduct : item
        )));
    };

    const openDifferenceReview = (product, competitorName, competitor) => {
        setDifferenceModal({ product, competitorName, competitor });
        setDifferenceReason(competitor.price_difference_reason || "manufacturer");
        setDifferenceNote(competitor.price_difference_note || "");
    };

    const saveDifferenceReview = async (reason) => {
        if (!differenceModal) {
            return;
        }
        setDifferenceSaving(true);
        try {
            const response = await updatePriceMonitorDifferenceReview(
                differenceModal.product.product_code,
                differenceModal.competitorName,
                { reason, note: reason ? differenceNote : null }
            );
            updateProductInList(response.data);
            setDifferenceModal(null);
            if (differenceState !== "all") {
                setOffset(0);
                await loadProducts();
            }
            setMessage(reason ? "Причина разницы сохранена." : "Отметка снята.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить отметку.");
        } finally {
            setDifferenceSaving(false);
        }
    };

    const renderDifferenceReview = (product, competitorName, competitor) => {
        const ownPrice = Number(product.catalog_price ?? product.our_price);
        const otherPrice = Number(competitor.price);
        const hasOwnPrice = product.catalog_price != null || product.our_price != null;
        if (!hasOwnPrice || competitor.price == null
            || !Number.isFinite(ownPrice)
            || !Number.isFinite(otherPrice) || ownPrice === otherPrice
            || competitorVerificationStatus(competitor) === "wrong") {
            return null;
        }
        const explained = Boolean(differenceReasonLabels[competitor.price_difference_reason]);
        const gap = otherPrice - ownPrice;
        const reasonTitle = explained
            ? [differenceReasonLabels[competitor.price_difference_reason], competitor.price_difference_note].filter(Boolean).join(": ")
            : "Указать, почему цена отличается";
        return (
            <div className="price-difference-review">
                <span title="Цена конкурента относительно нашей">{gap > 0 ? "+" : "-"}{formatMoney(Math.abs(gap))}</span>
                <button
                    type="button"
                    className={explained ? "is-explained" : ""}
                    title={reasonTitle}
                    onClick={() => openDifferenceReview(product, competitorName, competitor)}
                >
                    <BadgeCheck size={14} /> {explained ? "Объяснена" : "Объяснить"}
                </button>
            </div>
        );
    };

    const updateCompetitorVerification = async (product, competitorName, status) => {
        if (!product?.product_code || !competitorName) {
            return;
        }
        const key = `${product.product_code}:${competitorName}:verification`;
        setVerificationSavingKey(key);
        try {
            const response = await updatePriceMonitorCompetitorVerification(product.product_code, competitorName, {
                status,
                note: null,
            });
            updateProductInList(response.data);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить статус проверки.");
        } finally {
            setVerificationSavingKey("");
        }
    };

    const renderVerificationControls = (product, competitorName, competitor) => {
        const verificationStatus = competitorVerificationStatus(competitor);
        const verification = verificationMeta[verificationStatus];
        const verificationKey = `${product.product_code}:${competitorName}:verification`;
        const verificationTitle = [
            verification.label,
            competitor?.verification_note,
            competitor?.verified_by ? `by ${competitor.verified_by}` : "",
            competitor?.verified_at ? formatStatusDate(competitor.verified_at) : "",
        ].filter(Boolean).join(" · ");
        return (
            <div className="verification-controls" title={verificationTitle}>
                {verificationOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                        <button
                            key={option.status}
                            type="button"
                            className={verificationStatus === option.status ? "is-active" : ""}
                            title={option.title}
                            onClick={() => updateCompetitorVerification(product, competitorName, option.status)}
                            disabled={verificationSavingKey === verificationKey}
                        >
                            <Icon size={12} />
                        </button>
                    );
                })}
            </div>
        );
    };

    const openCompetitorModal = (product, competitor, fallbackName) => {
        const nextName = competitor?.name || fallbackName || "";
        setCompetitorModal({ product, competitorName: nextName });
        setCompetitorDraft({
            name: nextName,
            price: competitor?.price ?? "",
            url: competitor?.url || "",
            url_locked: competitor?.url_locked ?? Boolean(competitor?.url),
            url_source: competitor?.url_source || null,
        });
    };

    const closeCompetitorModal = () => {
        setCompetitorModal(null);
        setCompetitorDraft(emptyCompetitor);
    };

    const saveCompetitorCell = async () => {
        if (!competitorModal || !competitorDraft.name.trim()) {
            setMessage("Укажите название конкурента.");
            return;
        }
        setIsSaving(true);
        try {
            const response = await updatePriceMonitorCompetitor(
                competitorModal.product.product_code,
                competitorModal.competitorName || competitorDraft.name,
                {
                    name: competitorDraft.name.trim(),
                    price: normalizePrice(competitorDraft.price),
                    url: competitorDraft.url.trim() || null,
                    url_locked: true,
                }
            );
            updateProductInList(response.data);
            closeCompetitorModal();
            setMessage("Данные конкурента сохранены.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить конкурента.");
        } finally {
            setIsSaving(false);
        }
    };

    const saveAndRefreshCompetitorCell = async () => {
        if (!competitorModal || !competitorDraft.name.trim()) {
            setMessage("Укажите название конкурента.");
            return;
        }
        setIsSaving(true);
        try {
            const saved = await updatePriceMonitorCompetitor(
                competitorModal.product.product_code,
                competitorModal.competitorName || competitorDraft.name,
                {
                    name: competitorDraft.name.trim(),
                    price: normalizePrice(competitorDraft.price),
                    url: competitorDraft.url.trim() || null,
                    url_locked: true,
                }
            );
            updateProductInList(saved.data);
            const refreshed = await refreshPriceMonitorCompetitor(saved.data.product_code, competitorDraft.name.trim());
            if (refreshed.data.product) {
                updateProductInList(refreshed.data.product);
            }
            closeCompetitorModal();
            setMessage(refreshed.data.message);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить цену из источника.");
        } finally {
            setIsSaving(false);
        }
    };

    const refreshOwnPriceCell = async (product) => {
        const key = `${product.product_code}:own`;
        setRefreshingKey(key);
        try {
            const response = await refreshPriceMonitorOwnPrice(product.product_code);
            if (response.data.product) {
                updateProductInList(response.data.product);
            }
            setMessage(response.data.message);
            await loadOwnPriceRefreshStatus();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить нашу цену с azs.parts.");
        } finally {
            setRefreshingKey("");
        }
    };

    const refreshOwnPriceColumn = async () => {
        if (ownPriceRefreshStatus?.active) {
            setMessage("Обновление наших цен уже идет.");
            return;
        }
        setRefreshingKey("all:own");
        try {
            const response = await refreshPriceMonitorOwnPricesAll(favoriteOnly);
            setMessage(response.data.message);
            await loadOwnPriceRefreshStatus();
            await loadImportRuns();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось запустить обновление наших цен.");
        } finally {
            setRefreshingKey("");
        }
    };

    const refreshCompetitorCell = async (product, competitorName) => {
        if (!competitorName) {
            openCompetitorModal(product, emptyCompetitor, "");
            return;
        }
        const key = `${product.product_code}:${competitorName}`;
        setRefreshingKey(key);
        try {
            const response = await refreshPriceMonitorCompetitor(product.product_code, competitorName);
            if (response.data.product) {
                updateProductInList(response.data.product);
            }
            setMessage(response.data.message);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить цену из источника.");
        } finally {
            setRefreshingKey("");
        }
    };

    const refreshCompetitorColumn = async (competitorName) => {
        if (!competitorName || isPlaceholderCompetitor(competitorName)) {
            return;
        }
        const status = refreshStatuses[competitorName];
        if (status?.active) {
            setMessage("Обновление этого конкурента уже идет.");
            return;
        }
        setRefreshingKey(`all:${competitorName}`);
        try {
            const response = await refreshPriceMonitorCompetitorAll(competitorName, favoriteOnly);
            setMessage(response.data.message);
            await loadRefreshStatuses();
            if (isJsonLdCompetitor(competitorName)) {
                await loadImportRuns();
            } else {
                await Promise.all([loadProducts(), loadImportRuns()]);
            }
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить колонку конкурента.");
        } finally {
            setRefreshingKey("");
        }
    };

    const toggleFavorite = async (product) => {
        setFavoriteSavingCode(product.product_code);
        try {
            await updatePriceMonitorFavorite(product.product_code, !product.is_favorite);
            setProducts((current) => current.filter((item) => item.product_code !== product.product_code));
            setTotal((current) => Math.max(0, current - 1));
            if (products.length === 1 && offset > 0) {
                setOffset(Math.max(0, offset - pageSize));
            } else {
                await loadProducts();
            }
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось изменить избранное.");
        } finally {
            setFavoriteSavingCode("");
        }
    };

    const handleImportConfig = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        setIsImporting(true);
        try {
            const response = await importPriceMonitorConfig(file);
            setMessage(response.data.message);
            await loadProducts();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить конфиг.");
        } finally {
            setIsImporting(false);
        }
    };

    const handleDownloadConfig = async () => {
        setIsImporting(true);
        setMessage("");
        try {
            const response = await downloadPriceMonitorConfig();
            const filename = filenameFromResponse(
                response,
                `price-monitor-config-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`
            );
            downloadBlob(response.data, filename);
            setMessage("Конфиг скачан.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось скачать конфиг.");
        } finally {
            setIsImporting(false);
        }
    };

    const handleDownloadExcel = async () => {
        setIsImporting(true);
        setMessage("");
        try {
            const response = await downloadPriceMonitorExcel();
            const filename = filenameFromResponse(
                response,
                `price-monitor-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`
            );
            downloadBlob(response.data, filename);
            setMessage("Excel скачан.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось скачать Excel.");
        } finally {
            setIsImporting(false);
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        setIsImporting(true);
        try {
            const response = await importPriceMonitorExcel(file);
            setMessage(response.data.message);
            await loadProducts();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить Excel.");
        } finally {
            setIsImporting(false);
        }
    };

    const applySearch = (event) => {
        event.preventDefault();
        setOffset(0);
        setAppliedQuery(query.trim());
    };

    const applyTableFilter = (setter) => (event) => {
        setter(event.target.value);
        setOffset(0);
        cancelEdit();
    };

    const applyMinimumDifference = (event) => {
        event.preventDefault();
        const value = Number(minimumDifference);
        if (minimumDifference === "" || !Number.isFinite(value) || value < 0) {
            setMessage("Укажите порог разницы не меньше нуля.");
            return;
        }
        setAppliedMinimumDifference(String(value));
        setOffset(0);
        cancelEdit();
        if (differenceState === "all") {
            setDifferenceState("differences");
        }
    };

    const goToPage = (page) => {
        const targetPage = Math.min(Math.max(Number.parseInt(page, 10) || 1, 1), pageCount);
        setOffset((targetPage - 1) * pageSize);
        cancelEdit();
    };

    const handlePageJump = (event) => {
        event.preventDefault();
        goToPage(pageInput);
    };

    const renderPagination = (className = "") => (
        <div className={`price-monitor-pages ${className}`.trim()}>
            <div className="price-monitor-page-controls" aria-label="Навигация по страницам">
                <button className="price-monitor-page-icon" type="button" onClick={() => goToPage(1)} disabled={currentPage === 1} title="Первая страница" aria-label="Первая страница">
                    <ChevronsLeft size={16} />
                </button>
                <button className="price-monitor-page-icon" type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} title="Предыдущая страница" aria-label="Предыдущая страница">
                    <ChevronLeft size={16} />
                </button>
                {paginationItems.map((item) => (
                    typeof item === "string" ? (
                        <span className="price-monitor-page-ellipsis" key={item} aria-hidden="true">...</span>
                    ) : (
                        <button
                            className={`price-monitor-page-number${item === currentPage ? " is-current" : ""}`}
                            type="button"
                            key={item}
                            onClick={() => goToPage(item)}
                            aria-current={item === currentPage ? "page" : undefined}
                        >
                            {item}
                        </button>
                    )
                ))}
                <button className="price-monitor-page-icon" type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === pageCount} title="Следующая страница" aria-label="Следующая страница">
                    <ChevronRight size={16} />
                </button>
                <button className="price-monitor-page-icon" type="button" onClick={() => goToPage(pageCount)} disabled={currentPage === pageCount} title="Последняя страница" aria-label="Последняя страница">
                    <ChevronsRight size={16} />
                </button>
            </div>
            <span className="price-monitor-page-summary">{currentPage} / {pageCount} · {total} товаров</span>
            <form className="price-monitor-page-jump" onSubmit={handlePageJump}>
                <label htmlFor={`price-monitor-page-${className || "top"}`}>К странице</label>
                <input
                    id={`price-monitor-page-${className || "top"}`}
                    type="number"
                    min="1"
                    max={pageCount}
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value)}
                    aria-label="Номер страницы"
                />
                <button type="submit">Перейти</button>
            </form>
        </div>
    );

    const handleImportYml = async (event) => {
        event.preventDefault();
        const cleanImportUrl = importUrl.trim();
        if (
            importTarget === "competitor"
            && !importFile
            && /^https?:\/\/(www\.)?azs\.parts(?:\/|$)/i.test(cleanImportUrl)
        ) {
            setMessage("Нельзя импортировать наш azs.parts как конкурента. Выберите файл или ссылку конкурента.");
            return;
        }
        setIsImporting(true);
        setMessage("");
        try {
            const response = await importPriceMonitorFeed({
                sourceUrl: importFile ? "" : cleanImportUrl,
                importTarget,
                competitorName,
                file: importFile,
            });
            const targetLabel = importTarget === "competitor" ? `конкурент ${competitorName}` : "наши цены";
            setMessage(`Импорт завершен (${targetLabel}): ${response.data.offers_count} цен, ${response.data.categories_count} категорий.`);
            setOffset(0);
            setImportFile(null);
            await Promise.all([loadProducts(), loadImportRuns()]);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось импортировать прайс.");
            await loadImportRuns();
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <main className="crm-shell price-monitor-shell">
            <header className="crm-header price-monitor-header">
                <div>
                    <h1>{favoriteOnly ? "Избранные товары" : "Мониторинг цен"}</h1>
                </div>
                <nav className="price-monitor-views" aria-label="Разделы мониторинга цен">
                    <NavLink to="/price-monitoring" end>Общий список</NavLink>
                    <NavLink to="/price-monitoring/favorites"><Star size={14} /> Избранные</NavLink>
                </nav>
            </header>

            <section className="price-monitor-toolbar">
                <form className="price-monitor-search" onSubmit={applySearch}>
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Название, артикул или slug"
                    />
                    <button type="submit"><Search size={15} /> Найти</button>
                </form>
                <div className="price-monitor-filters">
                    <select value={priceState} onChange={applyTableFilter(setPriceState)}>
                        <option value="all">Все товары</option>
                        <option value="with_price">С ценой</option>
                        <option value="without_price">Без цены</option>
                    </select>
                    <select value={sortBy} onChange={applyTableFilter(setSortBy)}>
                        <option value="name">По названию</option>
                        <option value="code">По коду</option>
                        <option value="price">По цене</option>
                        <option value="updated">По обновлению</option>
                    </select>
                    <select value={sortDir} onChange={applyTableFilter(setSortDir)}>
                        <option value="asc">По возрастанию</option>
                        <option value="desc">По убыванию</option>
                    </select>
                </div>
                {renderPagination()}
            </section>

            <form className="price-difference-filters" onSubmit={applyMinimumDifference}>
                <strong>Разница цен</strong>
                <select value={differenceState} onChange={applyTableFilter(setDifferenceState)} aria-label="Показать расхождения">
                    <option value="all">Все товары</option>
                    <option value="differences">С расхождением</option>
                    <option value="unreviewed">Не разобраны</option>
                    <option value="explained">Объяснены</option>
                </select>
                <label>
                    От
                    <input
                        type="number"
                        min="0"
                        step={differenceUnit === "percent" ? "0.1" : "1"}
                        value={minimumDifference}
                        onChange={(event) => setMinimumDifference(event.target.value)}
                        aria-label="Минимальная разница цен"
                    />
                </label>
                <select value={differenceUnit} onChange={applyTableFilter(setDifferenceUnit)} aria-label="Единица разницы">
                    <option value="rub">₽</option>
                    <option value="percent">%</option>
                </select>
                <select value={differenceDirection} onChange={applyTableFilter(setDifferenceDirection)} aria-label="Направление разницы">
                    <option value="any">В обе стороны</option>
                    <option value="ours_lower">Наша ниже</option>
                    <option value="ours_higher">Наша выше</option>
                </select>
                <button type="submit">Применить</button>
            </form>

            {!favoriteOnly && <details className="price-import-panel">
                <summary className="price-import-summary">
                    <span className="price-import-summary-title"><UploadCloud size={16} /> Импорт и файлы</span>
                    {importRuns[0] && (
                        <span className="price-import-status">
                            Последний: {importRuns[0].import_target === "competitor" ? importRuns[0].competitor_name : "наши цены"},
                            {" "}{importRuns[0].status}, {importRuns[0].offers_count} цен,
                            {" "}{new Date(importRuns[0].finished_at || importRuns[0].started_at).toLocaleString("ru-RU")}
                        </span>
                    )}
                </summary>
                <div className="price-import-content">
                <div className="price-import-group price-import-source">
                    <span className="price-import-label">
                        <UploadCloud size={15} /> Импорт прайса
                    </span>
                    <form className={`price-import-form is-${importTarget}`} onSubmit={handleImportYml}>
                        <select value={importTarget} onChange={(event) => setImportTarget(event.target.value)}>
                            <option value="own">Наши цены</option>
                            <option value="competitor">Конкурент</option>
                        </select>
                        {importTarget === "competitor" && (
                            <input
                                value={competitorName}
                                onChange={(event) => setCompetitorName(event.target.value)}
                                placeholder="Название конкурента"
                            />
                        )}
                        <input
                            value={importUrl}
                            onChange={(event) => setImportUrl(event.target.value)}
                            placeholder="Ссылка на YML/XML"
                        />
                        <label className="price-import-file">
                            <input
                                accept=".xml,.yml,.yaml,text/xml,application/xml"
                                onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                                type="file"
                            />
                            {importFile ? importFile.name : "Файл"}
                        </label>
                        <button type="submit" disabled={isImporting}>
                            {isImporting ? "Импорт..." : "Импорт"}
                        </button>
                    </form>
                </div>
                <div className="price-import-group price-file-actions">
                    <span className="price-import-label">Excel</span>
                    <button type="button" onClick={handleDownloadExcel} disabled={isImporting}>
                        Скачать
                    </button>
                    <label>
                        Загрузить
                        <input accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" type="file" onChange={handleImportExcel} />
                    </label>
                </div>
                <div className="price-import-group price-config-actions">
                    <span className="price-import-label">Конфиг</span>
                    <button type="button" onClick={handleDownloadConfig} disabled={isImporting}>
                        Скачать
                    </button>
                    <label>
                        Загрузить
                        <input accept=".json,application/json" type="file" onChange={handleImportConfig} />
                    </label>
                </div>
                </div>
            </details>}

            {message && <p className="success-message price-monitor-message">{message}</p>}

            <section className="price-monitor-table-wrap">
                <table className="price-monitor-table">
                    <thead>
                        <tr>
                            <th>Товар</th>
                            <th>Фото</th>
                            <th>
                                <span className="competitor-head">
                                    <span className="competitor-head-main">
                                        Наша цена (YML)
                                        <button
                                            type="button"
                                            title={favoriteOnly ? "Обновить резервные цены избранных со страниц товаров" : "Обновить резервные цены всех товаров со страниц товаров"}
                                            onClick={refreshOwnPriceColumn}
                                            disabled={refreshingKey === "all:own" || ownPriceRefreshStatus?.active}
                                        >
                                            <RefreshCw size={13} />
                                        </button>
                                    </span>
                                    <small className="competitor-refresh-status">
                                        {ownPriceRefreshStatus?.active
                                            ? `идет ${ownPriceRefreshStatus.processed_count || 0}/${ownPriceRefreshStatus.total_count || 0}`
                                            : ownPriceRefreshStatus?.last_update_at
                                                ? `last: ${formatStatusDate(ownPriceRefreshStatus.last_update_at)}`
                                                : ""}
                                    </small>
                                </span>
                            </th>
                            {Array.from({ length: visibleCompetitorCount }).map((_, index) => {
                                const columnName = competitorColumnNames[index];
                                const canRefreshColumn = columnName && !isPlaceholderCompetitor(columnName);
                                const hasSavedXmlFeed = canRefreshColumn && !isJsonLdCompetitor(columnName);
                                const refreshStatus = refreshStatuses[columnName] || {};
                                const isColumnRefreshActive = Boolean(refreshStatus.active);
                                const lastUpdateText = refreshStatus.last_update_at
                                    ? `last: ${formatStatusDate(refreshStatus.last_update_at)}`
                                    : "";
                                return (
                                    <th key={index}>
                                        <span className="competitor-head">
                                            <span className="competitor-head-main">
                                                {columnName}
                                                {hasSavedXmlFeed && (
                                                    <a
                                                        href={getPriceMonitorCompetitorFeedUrl(columnName)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Открыть сохраненный XML/YML конкурента"
                                                    >
                                                        <ExternalLink size={13} />
                                                    </a>
                                                )}
                                                {canRefreshColumn && (
                                                    <button
                                                        type="button"
                                                        title={favoriteOnly ? "Обновить только избранные товары" : (isJsonLdCompetitor(columnName) ? "Запустить медленное обновление по 1 позиции раз в 1-2 минуты" : "Обновить все цены конкурента из источника")}
                                                        onClick={() => refreshCompetitorColumn(columnName)}
                                                        disabled={refreshingKey === `all:${columnName}` || isColumnRefreshActive}
                                                    >
                                                        <RefreshCw size={13} />
                                                    </button>
                                                )}
                                            </span>
                                            {canRefreshColumn && (
                                                <small className="competitor-refresh-status">
                                                    {isColumnRefreshActive
                                                        ? `идет ${refreshStatus.processed_count || 0}/${refreshStatus.total_count || 0}`
                                                        : lastUpdateText}
                                                </small>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={visibleCompetitorCount + 4}>Загрузка...</td>
                            </tr>
                        ) : products.length === 0 ? (
                            <tr><td colSpan={visibleCompetitorCount + 4}>{favoriteOnly ? "Избранных товаров пока нет." : "Товары не найдены."}</td></tr>
                        ) : products.map((product) => {
                            const isEditing = editingCode === product.product_code;
                            const competitors = isEditing ? draft.competitors : product.competitors || [];
                            return (
                                <React.Fragment key={product.product_code}>
                                    <tr>
                                        <td className="price-product-cell">
                                            <strong>{product.product_name}</strong>
                                            <span>#{product.product_code}{product.vendor ? ` · ${product.vendor}` : ""}{product.vendor_code ? ` · ${product.vendor_code}` : ""}</span>
                                            <a href={product.product_url} target="_blank" rel="noreferrer">
                                                Страница товара <ExternalLink size={13} />
                                            </a>
                                        </td>
                                        <td>
                                            {product.image_url && (
                                                <img className="price-product-image" src={product.image_url} alt="" loading="lazy" />
                                            )}
                                        </td>
                                        <td className="price-cell">
                                            {isEditing ? (
                                                <input
                                                    value={draft.our_price}
                                                    onChange={(event) => setDraft((current) => ({ ...current, our_price: event.target.value }))}
                                                    placeholder="Ручная цена"
                                                    title="Ручная цена для отдельной комплектации"
                                                    aria-label="Ручная цена для отдельной комплектации"
                                                />
                                            ) : (
                                                <div>
                                                    <div className="price-copy-line">
                                                        <span>{formatMoney(product.catalog_price ?? product.our_price)}</span>
                                                        <button type="button" title="Скопировать цену YML" onClick={() => copyPrice(product.catalog_price ?? product.our_price, `${product.product_code}:own`)}>
                                                            {copiedKey === `${product.product_code}:own` ? <Check size={14} /> : <Copy size={14} />}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Обновить резервную цену со страницы товара"
                                                            onClick={() => refreshOwnPriceCell(product)}
                                                            disabled={refreshingKey === `${product.product_code}:own`}
                                                        >
                                                            <RefreshCw size={14} />
                                                        </button>
                                                    </div>
                                                    {product.catalog_price && product.our_price && product.catalog_price !== product.our_price && (
                                                        <small className="price-catalog-hint">Цена CRM: {formatMoney(product.our_price)}</small>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        {Array.from({ length: visibleCompetitorCount }).map((_, index) => {
                                            const columnName = competitorColumnNames[index];
                                            const competitor = isEditing
                                                ? (competitors[index] || emptyCompetitor)
                                                : (findCompetitorByName(competitors, columnName) || emptyCompetitor);
                                            const effectiveCompetitorName = competitor.name || columnName;
                                            const hasCompetitorPrice = competitor.price !== null
                                                && competitor.price !== undefined
                                                && competitor.price !== "";
                                            const hasCompetitorData = hasCompetitorPrice || competitor.url || competitor.name;
                                            const refreshKey = `${product.product_code}:${effectiveCompetitorName}`;
                                            const verificationStatus = competitorVerificationStatus(competitor);
                                            const verification = verificationMeta[verificationStatus];
                                            return (
                                                <td className={`price-cell competitor-cell ${verification.className}`} key={index}>
                                                    {isEditing ? (
                                                        <div className="competitor-edit">
                                                            <input value={competitor.name} onChange={(event) => updateDraftCompetitor(index, "name", event.target.value)} placeholder="Название" />
                                                            <input value={competitor.price ?? ""} onChange={(event) => updateDraftCompetitor(index, "price", event.target.value)} placeholder="Цена" />
                                                            <input value={competitor.url || ""} onChange={(event) => updateDraftCompetitor(index, "url", event.target.value)} placeholder="Ссылка" />
                                                        </div>
                                                    ) : hasCompetitorData ? (
                                                        <div className="competitor-view">
                                                            <span>{competitor.name || "Конкурент"}</span>
                                                            <div className="price-copy-line">
                                                                {competitor.url ? (
                                                                    <a href={competitor.url} target="_blank" rel="noreferrer">{formatMoney(competitor.price)}</a>
                                                                ) : (
                                                                    <strong>{formatMoney(competitor.price)}</strong>
                                                                )}
                                                                <button type="button" title="Скопировать цену" onClick={() => copyPrice(competitor.price, `${product.product_code}:${index}`)}>
                                                                    {copiedKey === `${product.product_code}:${index}` ? <Check size={14} /> : <Copy size={14} />}
                                                                </button>
                                                                <button type="button" title="Обновить из источника" onClick={() => refreshCompetitorCell(product, effectiveCompetitorName)} disabled={refreshingKey === refreshKey}>
                                                                    <RefreshCw size={14} />
                                                                </button>
                                                                <button type="button" title="Редактировать ссылку и цену" onClick={() => openCompetitorModal(product, competitor, effectiveCompetitorName)}>
                                                                    <Pencil size={14} />
                                                                </button>
                                                            </div>
                                                            {renderVerificationControls(product, effectiveCompetitorName, competitor)}
                                                            {renderDifferenceReview(product, effectiveCompetitorName, competitor)}
                                                        </div>
                                                    ) : (
                                                        <div className="competitor-empty-actions">
                                                            <span className="price-muted">нет данных</span>
                                                            <button type="button" title="Указать ссылку вручную" onClick={() => openCompetitorModal(product, emptyCompetitor, effectiveCompetitorName)}>
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button type="button" title="Попробовать обновить из источника" onClick={() => refreshCompetitorCell(product, effectiveCompetitorName)} disabled={refreshingKey === refreshKey}>
                                                                <RefreshCw size={14} />
                                                            </button>
                                                            {renderVerificationControls(product, effectiveCompetitorName, competitor)}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="price-actions">
                                            <button
                                                className="price-favorite-button"
                                                type="button"
                                                title={favoriteOnly ? "Вернуть в общий список" : "Перенести в избранное"}
                                                aria-label={favoriteOnly ? "Вернуть в общий список" : "Перенести в избранное"}
                                                onClick={() => toggleFavorite(product)}
                                                disabled={favoriteSavingCode === product.product_code}
                                            ><Star size={16} fill={favoriteOnly ? "currentColor" : "none"} /></button>
                                            {isEditing ? (
                                                <>
                                                    <button type="button" onClick={() => saveProduct(product)} disabled={isSaving}><Save size={15} /> Сохранить</button>
                                                    <button type="button" onClick={cancelEdit}><X size={15} /> Отмена</button>
                                                </>
                                            ) : (
                                                <button type="button" onClick={() => startEdit(product)}><Pencil size={15} /> Править</button>
                                            )}
                                        </td>
                                    </tr>
                                    {isEditing && (
                                        <tr className="price-edit-row">
                                            <td colSpan={visibleCompetitorCount + 4}>
                                                <div className="price-edit-panel">
                                                    <textarea
                                                        value={draft.note}
                                                        onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
                                                        placeholder="Заметка по товару или проверке цены"
                                                        rows={2}
                                                    />
                                                    <div className="price-edit-actions">
                                                        <button type="button" onClick={addCompetitor}><Plus size={15} /> Конкурент</button>
                                                        {draft.competitors.length > 3 && (
                                                            <button type="button" onClick={() => removeCompetitor(draft.competitors.length - 1)}>
                                                                <X size={15} /> Убрать последний
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            {total > pageSize && renderPagination("is-bottom")}

            {differenceModal && (
                <div className="price-modal-backdrop" role="presentation" onMouseDown={() => setDifferenceModal(null)}>
                    <div className="price-modal" role="dialog" aria-modal="true" aria-label="Объяснение разницы цен" onMouseDown={(event) => event.stopPropagation()}>
                        <header>
                            <div>
                                <strong>{differenceModal.product.product_name}</strong>
                                <span>{differenceModal.competitorName}: {formatMoney(differenceModal.competitor.price)}</span>
                            </div>
                            <button type="button" onClick={() => setDifferenceModal(null)} title="Закрыть" aria-label="Закрыть">
                                <X size={16} />
                            </button>
                        </header>
                        <label>
                            Почему цена отличается
                            <select value={differenceReason} onChange={(event) => setDifferenceReason(event.target.value)}>
                                {Object.entries(differenceReasonLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Комментарий
                            <textarea
                                value={differenceNote}
                                onChange={(event) => setDifferenceNote(event.target.value)}
                                maxLength={500}
                                rows={3}
                                placeholder="Например, другая комплектация или поставщик"
                            />
                        </label>
                        <footer>
                            {differenceModal.competitor.price_difference_reason && (
                                <button type="button" onClick={() => saveDifferenceReview(null)} disabled={differenceSaving}>
                                    Снять отметку
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => saveDifferenceReview(differenceReason)}
                                disabled={differenceSaving || (differenceReason === "other" && !differenceNote.trim())}
                            >
                                <Save size={15} /> Сохранить
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {competitorModal && (
                <div className="price-modal-backdrop" role="presentation" onMouseDown={closeCompetitorModal}>
                    <div className="price-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <header>
                            <div>
                                <strong>{competitorModal.product.product_name}</strong>
                                <span>#{competitorModal.product.product_code}</span>
                            </div>
                            <button type="button" onClick={closeCompetitorModal} title="Закрыть">
                                <X size={16} />
                            </button>
                        </header>
                        <label>
                            Конкурент
                            <input
                                value={competitorDraft.name}
                                onChange={(event) => setCompetitorDraft((current) => ({ ...current, name: event.target.value }))}
                                placeholder="Vinso"
                            />
                        </label>
                        <label>
                            Цена
                            <input
                                value={competitorDraft.price ?? ""}
                                onChange={(event) => setCompetitorDraft((current) => ({ ...current, price: event.target.value }))}
                                placeholder="0"
                            />
                        </label>
                        <label>
                            Ссылка на страницу конкурента
                            <input
                                value={competitorDraft.url || ""}
                                onChange={(event) => setCompetitorDraft((current) => ({ ...current, url: event.target.value }))}
                                placeholder="https://..."
                            />
                        </label>
                        <small className="price-modal-note">Ручная ссылка сохранится и не будет перезаписана массовым обновлением.</small>
                        <footer>
                            <button type="button" onClick={saveAndRefreshCompetitorCell} disabled={isSaving || Boolean(refreshingKey) || !competitorDraft.name.trim()}>
                                <RefreshCw size={15} /> Обновить из источника
                            </button>
                            <button type="button" onClick={saveCompetitorCell} disabled={isSaving}>
                                <Save size={15} /> Сохранить
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </main>
    );
};

export default PriceMonitoring;
